
import React, { useState, useEffect } from 'react';
import { AdConfig } from '../types';
import { SponsoredService } from '../services/sponsoredService';
import { X, ExternalLink, Timer, Sparkles, Megaphone, Info, Lock, ShieldCheck, ArrowUpRight, ShieldAlert, Play } from 'lucide-react';
import { useNavigate } from 'react-router';
import { useAuth } from './AuthBridge';
import { AdUnit } from './AdUnit';
import { useThemeTokens } from '../hooks/useActiveTheme';

interface AdOverlayProps {
  onClose: () => void;
}

export const AdOverlay: React.FC<AdOverlayProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const { user, activeCert } = useAuth();
  const tokens = useThemeTokens();
  const [config, setConfig] = useState<AdConfig>(SponsoredService.getConfig());
  const [timeLeft, setTimeLeft] = useState(config.overlaySettings.skipSeconds);
  const [canSkip, setCanSkip] = useState(false);

  useEffect(() => {
    const handleUpdate = () => setConfig(SponsoredService.getConfig());
    window.addEventListener('sponsored-config-updated', handleUpdate);
    
    // Ensure we have the latest config from the server on mount
    SponsoredService.fetchLatestConfig().then(latest => {
      setConfig(latest);
    });
    
    return () => window.removeEventListener('sponsored-config-updated', handleUpdate);
  }, []);

  useEffect(() => {
    // Force scroll to top so the overlay is visible immediately
    window.scrollTo(0, 0);
    
    // Prevent background scrolling
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';
    
    return () => {
        document.body.style.overflow = originalStyle;
    };
  }, []);

  useEffect(() => {
    if (timeLeft <= 0) {
      setCanSkip(true);
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  const handleUpgradeClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(`/${activeCert?.id || user?.activeCertId || 'cna'}/elite`);
    onClose();
  };

  const renderMedia = () => {
    if (config.type === 'video') {
      return (
        <div className="relative aspect-video w-full overflow-hidden bg-black rounded-2xl md:rounded-[2.5rem] shadow-2xl ring-1 ring-white/20">
          <iframe 
            src={`${config.mediaUrl}${config.mediaUrl.includes('?') ? '&' : '?'}autoplay=1&mute=1`} 
            className="w-full h-full" 
            allow="autoplay" 
            title="Sponsored Content" 
          />
        </div>
      );
    }
    if (config.type === 'image') {
      return (
        <div className="relative w-full aspect-video rounded-2xl md:rounded-[2.5rem] overflow-hidden shadow-2xl border-2 md:border-8 border-white ring-4 ring-black/40">
          <img src={config.mediaUrl} alt="Advertisement" className="w-full h-full object-cover transition-transform duration-[10s] group-hover:scale-110" />
        </div>
      );
    }
    return (
      <div className="w-full p-12 md:p-16 bg-slate-900 rounded-2xl md:rounded-[2.5rem] border-4 border-dashed border-slate-800 flex flex-col items-center text-center shadow-inner">
        <Megaphone size={80} className="text-slate-800 mb-6" />
        <p className="text-slate-500 font-black tracking-widest text-xs md:text-sm">Educational Partner Update</p>
      </div>
    );
  };

  const isNetworkSource = config.placements.overlay.source === 'network';

  return (
    <div className={`fixed inset-0 z-[10000] ${tokens.heroBannerBg} flex flex-col animate-fade-in overflow-hidden`}>
      {/* Background Ambience */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-30">
        <div className="absolute -top-[10%] -left-[10%] w-[40%] h-[40%] bg-medical-600/20 blur-[120px] rounded-full"></div>
        <div className="absolute -bottom-[10%] -right-[10%] w-[40%] h-[40%] bg-indigo-600/20 blur-[120px] rounded-full"></div>
      </div>
      
      {/* Protocol Header Bar */}
      <div className="relative w-full px-4 py-3 md:px-10 md:py-6 flex justify-between items-center text-white z-[10001] pt-safe shrink-0 bg-slate-950/40 backdrop-blur-md border-b border-white/5">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 md:w-12 md:h-12 bg-medical-500 rounded-lg md:rounded-[1.2rem] flex items-center justify-center shadow-xl ring-1 ring-white/20">
            <Sparkles size={24} className="text-white" />
          </div>
          <div className="min-w-0">
            <h2 className="text-[10px] md:sm font-black tracking-widest text-white truncate">Clinical Intermission</h2>
            <p className="text-[7px] md:text-[10px] text-slate-400 font-bold tracking-widest mt-0.5">Academic Bridge</p>
          </div>
        </div>

        {!canSkip && (
          <div className="bg-white/5 backdrop-blur-2xl px-3 py-1.5 md:px-6 md:py-4 rounded-xl md:rounded-2xl border border-white/10 flex items-center gap-3 animate-pop shadow-xl">
            <div className="relative flex items-center justify-center w-6 h-6 md:w-10 md:h-10">
               <svg className="w-full h-full rotate-[-90deg]">
                  <circle cx="50%" cy="50%" r="42%" stroke="currentColor" strokeWidth="3" fill="transparent" className="text-white/5" />
                  <circle 
                      cx="50%" cy="50%" r="42%" stroke="currentColor" strokeWidth="3" fill="transparent" 
                      className="text-medical-500"
                      strokeDasharray="113"
                      strokeDashoffset={113 - (113 * (timeLeft / Math.max(1, config.overlaySettings.skipSeconds)))}
                      strokeLinecap="round"
                  />
               </svg>
               <span className="absolute text-[8px] md:text-[12px] font-black">{timeLeft}</span>
            </div>
            <div className="hidden xs:flex flex-col">
              <span className="text-[7px] md:text-[9px] font-black text-slate-300 tracking-widest leading-none">Auto-Lock</span>
              <span className="text-[9px] md:text-xs font-black text-white tracking-widest">Active</span>
            </div>
          </div>
        )}
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto custom-scrollbar flex flex-col items-center justify-start md:justify-center p-3 sm:p-6 md:p-12 relative z-[10002]">
        <div className="max-w-4xl w-full my-auto flex flex-col items-center space-y-4 sm:space-y-6 md:space-y-10 animate-slide-up py-4">
          {isNetworkSource ? (
            <div className="w-full bg-white rounded-2xl md:rounded-[2.5rem] p-2 sm:p-4 md:p-8 shadow-2xl border border-slate-100 overflow-hidden">
               <AdUnit placement="overlay" />
            </div>
          ) : (
            <>
              <div className="w-full max-w-2xl relative group">
                 {renderMedia()}
                 <div className="absolute -bottom-8 -right-8 w-32 h-32 md:w-48 md:h-48 bg-medical-500/10 blur-[80px] rounded-full pointer-events-none"></div>
                 <div className="absolute -top-8 -left-8 w-32 h-32 md:w-48 md:h-48 bg-indigo-500/10 blur-[80px] rounded-full pointer-events-none"></div>
              </div>

              <div className="text-center space-y-3 md:space-y-5 max-w-3xl px-2">
                <div className="flex items-center justify-center gap-2 md:gap-3 opacity-40">
                  <div className="h-px w-6 md:w-12 bg-white/30"></div>
                  <div className="flex items-center gap-2">
                      <ShieldCheck size={14} className="text-medical-400 shrink-0" />
                      <span className="text-[8px] md:text-[10px] font-black text-slate-200 tracking-widest uppercase">Educational Focus</span>
                  </div>
                  <div className="h-px w-6 md:w-12 bg-white/30"></div>
                </div>
                
                <h3 className="text-xl sm:text-3xl md:text-5xl font-black text-white tracking-tight leading-snug break-words">{config.title}</h3>
                <p className="text-xs sm:text-base md:text-xl text-white font-medium sm:font-bold leading-relaxed max-w-2xl mx-auto drop-shadow-lg opacity-90 break-words">"{config.description}"</p>
                
                <div className="pt-2 md:pt-4">
                  <a 
                    href={config.targetUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={`w-full sm:w-auto px-6 py-3.5 sm:px-10 sm:py-4 md:px-14 md:py-5 text-white rounded-xl md:rounded-2xl font-black text-xs sm:text-sm md:text-base tracking-widest shadow-2xl transition-all inline-flex items-center justify-center gap-2 md:gap-3 active:scale-95 group border-b-2 md:border-b-4 ${tokens.primaryBtn}`}
                  >
                    <span>{config.ctaText || 'Learn More'}</span>
                    <ExternalLink size={18} className="group-hover:scale-110 transition-transform shrink-0" />
                  </a>
                </div>
                
                {config.showUpgradeHint && (
                  <button 
                    onClick={handleUpgradeClick} 
                    className="mt-3 md:mt-6 flex items-center justify-center gap-2 text-white/50 hover:text-white transition-all text-[8px] sm:text-[10px] font-black tracking-widest mx-auto animate-fade-in group min-h-[36px] px-2"
                  >
                      <ShieldAlert size={14} className="group-hover:scale-110 transition-transform text-indigo-400 shrink-0" /> 
                      <span>Remove All Ads With Elite Pro</span>
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Persistence Bar / Skip Footer */}
      <div className="relative w-full px-4 py-4 md:px-10 md:py-10 flex flex-col items-center gap-4 bg-slate-950/80 backdrop-blur-xl border-t border-white/5 pb-safe shrink-0">
        {!canSkip ? (
          <div className="w-full max-w-xs md:max-w-md space-y-3">
            <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden shadow-inner">
              <div 
                className="h-full bg-medical-500 transition-all duration-1000 ease-linear shadow-[0_0_15px_rgba(14,165,233,0.5)]" 
                style={{ width: `${(timeLeft / Math.max(1, config.overlaySettings.skipSeconds)) * 100}%` }}
              ></div>
            </div>
            <p className="text-[7px] font-black text-slate-500 tracking-[0.3em] md:tracking-[0.4em] flex items-center justify-center gap-2">
              <Info size={12}/> Logic Synchronization Resuming
            </p>
          </div>
        ) : (
          <button 
            onClick={onClose}
            className="w-full md:w-auto bg-white text-slate-950 px-8 py-4 md:px-12 md:py-6 rounded-xl md:rounded-3xl font-black text-xs md:sm tracking-[0.2em] flex items-center justify-center gap-3 hover:bg-emerald-50 transition-all shadow-white/10 shadow-2xl animate-pop active:scale-95 border-b-4 border-slate-200"
          >
            Continue Drills <Play size={20} fill="currentColor" />
          </button>
        )}
      </div>
    </div>
  );
};
